import React, { useState, useEffect } from 'react';
import { useDatabase } from '../db/DatabaseProvider';
import { performBcvSync } from '../db/sync/bcvSync';
import { useAuthStore } from '../stores/auth.store';
import { useToast } from '../components/common/ToastProvider';
import { generateId, now } from '../lib/uuid';
import { 
  Building2, 
  Coins, 
  Settings, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft, 
  Loader2, 
  Wallet, 
  FolderHeart,
  Plus,
  Trash2,
  Calendar,
  Sparkles
} from 'lucide-react';

interface CustomItem {
  id: string;
  name: string;
  type?: 'income' | 'expense' | 'both';
  currency?: string;
  color?: string;
  icon?: string;
}

const PRESET_COLORS = ['#10B981', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#6B7280'];
const PRESET_ICONS = ['💼', '📦', '👥', '💸', '🏢', '⚡', '🛠️', '🛒', '💰', '🩺', '✂️', '💅'];

export function SetupWizard() {
  const db = useDatabase();
  const user = useAuthStore((s) => s.user);
  const toast = useToast();

  // Stepper state
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  // Step 1: Branch Config
  const [branchName, setBranchName] = useState('');
  const [branchCode, setBranchCode] = useState('');

  // Step 2: Currency & Rates Config
  const minSupportedYear = 2022; // Safety barrier limit
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from(
    { length: Math.max(1, currentYear - minSupportedYear + 1) },
    (_, i) => currentYear - i
  );
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [startYear, setStartYear] = useState(String(currentYear));

  // Step 3: Default Catalog Options
  const [loadDefaultCategories, setLoadDefaultCategories] = useState(true);
  const [loadDefaultPaymentMethods, setLoadDefaultPaymentMethods] = useState(true);

  // Custom lists (if defaults unchecked)
  const [customCategories, setCustomCategories] = useState<CustomItem[]>([
    { id: 'cat-custom-services', name: 'Servicios', type: 'income', color: '#10B981', icon: '💼' },
    { id: 'cat-custom-gastos', name: 'Gastos Administrativos', type: 'expense', color: '#EF4444', icon: '📦' }
  ]);
  const [customPaymentMethods, setCustomPaymentMethods] = useState<CustomItem[]>([
    { id: 'mp-custom-efectivo-ves', name: 'Efectivo', currency: 'VES' },
    { id: 'mp-custom-zelle', name: 'Zelle', currency: 'USD' }
  ]);

  // Temp form values for adding custom items
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState<'income' | 'expense'>('income');
  const [selectedColor, setSelectedColor] = useState('#10B981');
  const [selectedIcon, setSelectedIcon] = useState('💼');
  
  const [newMpName, setNewMpName] = useState('');
  const [newMpCurrency, setNewMpCurrency] = useState('VES');

  // ── 1. LocalStorage Progress Retention ──────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem('depilaway-setup-progress');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.step) setStep(data.step);
        if (data.branchName) setBranchName(data.branchName);
        if (data.branchCode) setBranchCode(data.branchCode);
        if (data.baseCurrency) setBaseCurrency(data.baseCurrency);
        if (data.startYear) setStartYear(data.startYear);
        if (data.loadDefaultCategories !== undefined) setLoadDefaultCategories(data.loadDefaultCategories);
        if (data.loadDefaultPaymentMethods !== undefined) setLoadDefaultPaymentMethods(data.loadDefaultPaymentMethods);
        if (data.customCategories) setCustomCategories(data.customCategories);
        if (data.customPaymentMethods) setCustomPaymentMethods(data.customPaymentMethods);
      } catch (e) {
        console.warn('Failed to parse saved setup progress:', e);
      }
    }
  }, []);

  useEffect(() => {
    const data = {
      step,
      branchName,
      branchCode,
      baseCurrency,
      startYear,
      loadDefaultCategories,
      loadDefaultPaymentMethods,
      customCategories,
      customPaymentMethods,
    };
    localStorage.setItem('depilaway-setup-progress', JSON.stringify(data));
  }, [
    step,
    branchName,
    branchCode,
    baseCurrency,
    startYear,
    loadDefaultCategories,
    loadDefaultPaymentMethods,
    customCategories,
    customPaymentMethods,
  ]);

  // Step 1 input validation
  const isStep1Valid = branchName.trim().length > 0 && branchCode.trim().length > 0;

  // Add custom category
  const handleAddCategory = () => {
    if (!newCatName.trim()) return;
    setCustomCategories((prev) => [
      ...prev,
      {
        id: 'cat-custom-' + generateId(),
        name: newCatName.trim(),
        type: newCatType,
        color: selectedColor,
        icon: selectedIcon
      }
    ]);
    setNewCatName('');
  };

  // Remove custom category
  const handleRemoveCategory = (id: string) => {
    setCustomCategories((prev) => prev.filter((c) => c.id !== id));
  };

  // Add custom payment method
  const handleAddPaymentMethod = () => {
    if (!newMpName.trim()) return;
    setCustomPaymentMethods((prev) => [
      ...prev,
      {
        id: 'mp-custom-' + generateId(),
        name: newMpName.trim(),
        currency: newMpCurrency
      }
    ]);
    setNewMpName('');
  };

  // Remove custom payment method
  const handleRemovePaymentMethod = (id: string) => {
    setCustomPaymentMethods((prev) => prev.filter((m) => m.id !== id));
  };

  const handleNext = () => {
    if (step === 1 && !isStep1Valid) {
      toast.warning('Por favor, ingresa el nombre y código de tu sede principal.', 'Campos Requeridos');
      return;
    }
    if (step === 3) {
      if (!loadDefaultCategories && customCategories.length === 0) {
        toast.warning('Por favor, agrega al menos una categoría personalizada o marca la opción estándar.', 'Categorías Requeridas');
        return;
      }
      if (!loadDefaultPaymentMethods && customPaymentMethods.length === 0) {
        toast.warning('Por favor, agrega al menos un método de pago personalizado o marca la opción estándar.', 'Métodos de Pago Requeridos');
        return;
      }
    }
    setStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    setStep((prev) => prev - 1);
  };

  const handleFinish = async () => {
    if (!db) return;
    setIsSubmitting(true);
    setSubmitStatus('Preparando el almacenamiento en este equipo...');

    try {
      const timestamp = now();
      const adminUser = user?.email || 'setup_wizard';

      // 1. Create or update configured branch
      const branchId = 'branch-' + branchCode.toLowerCase().trim();
      await db.branches.upsert({
        id: branchId,
        name: branchName.trim(),
        code: branchCode.toUpperCase().trim(),
        is_active: true,
        updated_at: timestamp,
        is_deleted: false,
        last_modified_by: adminUser,
      });

      // 2. Insert or update Base configurations
      const defaultConfigs = [
        { id: 'config-currency-base', key: 'base_currency', value: baseCurrency, owner_only: true },
        { id: 'config-currency-local', key: 'local_currency', value: 'VES', owner_only: true },
        { id: 'config-active-currencies', key: 'active_currencies', value: `VES,${baseCurrency}`, owner_only: true },
        { id: 'config-reconciliation-tolerance', key: 'reconciliation_tolerance', value: '0.02', owner_only: true },
        { id: 'config-roles-staff', key: 'staff_roles', value: JSON.stringify(['Administrador', 'Recepcionista', 'Operador']), owner_only: false },
        { id: 'config-support-contact', key: 'support_contact', value: 'soporte@depilaway.com (WhatsApp: +58 412 1234567)', owner_only: false },
      ];

      for (const config of defaultConfigs) {
        await db.app_config.upsert({
          ...config,
          updated_at: timestamp,
          is_deleted: false,
          last_modified_by: adminUser,
        });
      }

      // 3. Seed Categories
      if (loadDefaultCategories) {
        setSubmitStatus('Cargando categorías predeterminadas...');
        const defaultCategories: Array<{ id: string; name: string; type: 'income' | 'expense' | 'both'; color: string; icon: string }> = [
          { id: 'cat-servicios', name: 'Ingresos por Servicios', type: 'income', color: '#10B981', icon: '💆‍♀️' },
          { id: 'cat-paquetes', name: 'Ingresos por Paquetes / Combos', type: 'income', color: '#06B6D4', icon: '📦' },
          { id: 'cat-nomina', name: 'Nómina / Sueldos', type: 'expense', color: '#F59E0B', icon: '👥' },
          { id: 'cat-insumos', name: 'Insumos / Materiales', type: 'expense', color: '#8B5CF6', icon: '🧴' },
          { id: 'cat-alquiler', name: 'Alquiler y Mantenimiento', type: 'expense', color: '#EC4899', icon: '🏢' },
        ];
        for (const cat of defaultCategories) {
          await db.categories.upsert({
            ...cat,
            is_active: true,
            updated_at: timestamp,
            is_deleted: false,
            last_modified_by: adminUser,
          });
        }
      } else {
        setSubmitStatus('Cargando categorías personalizadas...');
        for (const cat of customCategories) {
          await db.categories.upsert({
            id: cat.id,
            name: cat.name,
            type: cat.type as 'income' | 'expense',
            is_active: true,
            color: cat.color || '#3B82F6',
            icon: cat.type === 'income' ? '💼' : '📦',
            updated_at: timestamp,
            is_deleted: false,
            last_modified_by: adminUser,
          });
        }
      }

      // 4. Seed Payment Methods
      if (loadDefaultPaymentMethods) {
        setSubmitStatus('Cargando métodos de pago predeterminados...');
        const defaultPaymentMethods: Array<{ id: string; name: string; type: 'local' | 'foreign'; currency: string }> = [
          { id: 'wallet-efectivo-ves', name: 'Efectivo', type: 'local', currency: 'VES' },
          { id: 'wallet-provincial', name: 'Provincial', type: 'local', currency: 'VES' },
          { id: 'wallet-banesco', name: 'Banesco', type: 'local', currency: 'VES' },
          { id: `wallet-cash-${baseCurrency.toLowerCase()}`, name: 'Efectivo', type: 'foreign', currency: baseCurrency },
          { id: `wallet-binance-${baseCurrency.toLowerCase()}`, name: 'Binance Pay', type: 'foreign', currency: baseCurrency },
          { id: 'wallet-zelle', name: 'Zelle', type: 'foreign', currency: 'USD' },
          { id: 'wallet-paypal', name: 'Paypal / Zinli', type: 'foreign', currency: 'USD' },
        ];
        for (const method of defaultPaymentMethods) {
          await db.payment_methods.upsert({
            ...method,
            is_active: true,
            updated_at: timestamp,
            is_deleted: false,
            last_modified_by: adminUser,
          });
        }
      } else {
        setSubmitStatus('Cargando métodos de pago personalizados...');
        for (const mp of customPaymentMethods) {
          await db.payment_methods.upsert({
            id: mp.id,
            name: mp.name,
            type: mp.currency === 'VES' ? 'local' : 'foreign',
            currency: mp.currency || 'VES',
            is_active: true,
            updated_at: timestamp,
            is_deleted: false,
            last_modified_by: adminUser,
          });
        }
      }

      // 5. Fetch official rates from BCV for initial setup
      console.log(`🚀 [SetupWizard] Sincronizando tasas oficiales del BCV para año de inicio: ${startYear}`);
      setSubmitStatus(`Sincronizando tasas de cambio oficiales del BCV (${startYear})...`);
      let fetchedRates = false;
      try {
        const syncRes = await performBcvSync(db, 'bcv_sync_setup', `${startYear}-01-01`);
        console.log(`✅ [SetupWizard] Resultado de sincronización BCV:`, syncRes);
        if (syncRes.status === 'success' || syncRes.status === 'up_to_date') {
          fetchedRates = true;
        }
      } catch (err) {
        console.warn('❌ [SetupWizard] Falló la sincronización de tasas BCV:', err);
      }

      // If rates fetch failed or returned nothing, seed a single fallback rate for today
      if (!fetchedRates) {
        setSubmitStatus('Insertando tasa cambiaria inicial de contingencia...');
        const existingFallback = await db.exchange_rates.findOne('bcv_2026-01-02').exec();
        if (!existingFallback) {
          await db.exchange_rates.upsert({
            id: 'bcv_2026-01-02',
            since: '2026-01-03',
            until: '2026-01-05',
            eur: 357.88579854,
            usd: 304.6796,
            updated_at: timestamp,
            is_deleted: false,
            last_modified_by: adminUser,
          });
        }
      }

      // 7. Mark system as initialized locally and in Supabase
      setSubmitStatus('Finalizando inicialización del sistema...');
      await db.app_config.upsert({
        id: 'config-sistema-start-year',
        key: 'sistema_start_year',
        value: String(startYear),
        owner_only: false,
        updated_at: timestamp,
        is_deleted: false,
        last_modified_by: adminUser,
      });

      await db.app_config.upsert({
        id: 'config-sistema-inicializado',
        key: 'system_initialized',
        value: 'true',
        owner_only: false,
        updated_at: timestamp,
        is_deleted: false,
        last_modified_by: adminUser,
      });

      // Clear setup progress from localStorage
      localStorage.removeItem('depilaway-setup-progress');

      // Give sync store a moment to push initial changes
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      // Force reload to update store, sync states and boot the app shell
      globalThis.location.reload();
    } catch (err: any) {
      console.error('Failed to initialize database during setup wizard:', err);
      toast.error('Ocurrió un error al guardar la configuración inicial: ' + (err.message || 'Error desconocido'), 'Error de Inicialización');
      setSubmitStatus('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F3F8FF] p-4 dark:bg-[#244C5A] font-sans selection:bg-brand-orange selection:text-white transition-colors duration-300">
      <div className="w-full max-w-xl rounded-2xl border border-border/80 bg-white p-6 shadow-xl dark:bg-[#1a3a45]/90 backdrop-blur-md transition-all animate-fade-in">
        
        {/* Header Indicator */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xs font-black uppercase tracking-widest text-brand-orange flex items-center gap-1">
              <Sparkles className="h-4 w-4 text-brand-orange animate-pulse" />
              Configuración Inicial
            </span>
            <span className="text-xs font-bold text-text-tertiary">
              Paso {step} de 4
            </span>
          </div>
          <div className="h-1.5 w-full bg-border/40 rounded-full overflow-hidden">
            <div 
              className="h-full bg-brand-orange transition-all duration-500 rounded-full"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* STEP 1: Branch Setup */}
        {step === 1 && (
          <div className="space-y-4 animate-scale-in">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-brand-orange/10 text-brand-orange shadow-sm">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-text-primary">Tu Sede Principal</h3>
                <p className="text-xs text-text-secondary">Establece el local de operaciones primario de tu negocio.</p>
              </div>
            </div>
            <div className="grid gap-3 pt-2">
              <div>
                <label className="block text-xs font-bold text-text-secondary mb-1">Nombre de la Sede</label>
                <input 
                  type="text" 
                  value={branchName}
                  onChange={(e) => setBranchName(e.target.value)}
                  placeholder="Ej. Sede Central, Oficina Principal, Centro"
                  className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-text-secondary mb-1">Código de la Sede</label>
                <input 
                  type="text" 
                  maxLength={5}
                  value={branchCode}
                  onChange={(e) => setBranchCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                  placeholder="Ej. HQ, CCS, PPL"
                  className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 font-mono font-bold"
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Currency & Historical Rates Year Setup */}
        {step === 2 && (
          <div className="space-y-4 animate-scale-in">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-brand-orange/10 text-brand-orange shadow-sm">
                <Coins className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-text-primary">Monedas e Historial</h3>
                <p className="text-xs text-text-secondary">Configura el estándar monetario para tu contabilidad.</p>
              </div>
            </div>
            <div className="grid gap-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-text-secondary mb-1">Moneda Base (Contable)</label>
                  <select 
                    value={baseCurrency}
                    onChange={(e) => setBaseCurrency(e.target.value)}
                    className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                  >
                    <option value="USD">Dólar ($)</option>
                    <option value="EUR">Euro (€)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-text-secondary mb-1">Moneda Local</label>
                  <div className="w-full rounded-xl border border-border bg-surface-secondary px-3 py-2 text-sm text-text-primary font-semibold select-none">
                    Bolívar (VES)
                  </div>
                </div>
              </div>

              <div className="border-t border-border/40 pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-brand-orange" />
                  <span className="text-xs font-bold text-text-secondary">Sincronización de Historial Cambiario (BCV)</span>
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    Selecciona desde qué año deseas descargar el historial diario de tasas cambiarias de la API oficial del BCV:
                  </label>
                  <select 
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value)}
                    className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 font-bold"
                  >
                    {availableYears.map((yr) => (
                      <option key={yr} value={String(yr)}>
                        {yr} {yr === currentYear ? '(Año actual - Recomendado)' : yr === currentYear - 1 ? '(Año anterior)' : '(Histórico)'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Catalog Setup (Defaults vs Custom) */}
        {step === 3 && (
          <div className="space-y-4 animate-scale-in max-h-[60vh] overflow-y-auto pr-1">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-brand-orange/10 text-brand-orange shadow-sm">
                <Settings className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-text-primary">Estructura de Catálogos</h3>
                <p className="text-xs text-text-secondary">Elige cargar valores estándar para cualquier negocio de services o ingresa los tuyos.</p>
              </div>
            </div>

            {/* SECTION A: Categories */}
            <div className="border-t border-border/40 pt-3 space-y-3">
              <label className="flex items-start gap-3 rounded-xl border border-border bg-surface-secondary/20 p-3 hover:bg-surface-secondary/40 transition-colors cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={loadDefaultCategories}
                  onChange={(e) => setLoadDefaultCategories(e.target.checked)}
                  className="mt-1 rounded border-border text-brand-orange focus:ring-brand-orange/20"
                />
                <div>
                  <span className="flex items-center gap-1.5 text-sm font-bold text-text-primary">
                    <FolderHeart className="h-4 w-4 text-brand-orange" />
                    Categorías de Cuentas Estándar
                  </span>
                  <p className="text-[11px] text-text-secondary mt-0.5">
                    Pre-cargar categorías generales (Ventas de Servicios, Productos, Nóminas, Comisiones, Alquiler, Servicios Públicos y Gastos Varios).
                  </p>
                </div>
              </label>

              {!loadDefaultCategories && (
                <div className="rounded-xl border border-dashed border-border p-3 bg-surface space-y-3 animate-fade-in">
                  <span className="text-xs font-bold text-brand-orange block">Categorías Personalizadas</span>
                  
                  {/* Category List */}
                  {customCategories.length > 0 ? (
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto pb-1">
                      {customCategories.map((cat) => (
                        <div key={cat.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-surface-secondary text-text-primary border border-border">
                          <span style={{ borderLeft: `3px solid ${cat.color}`, paddingLeft: '4px' }}>
                            {cat.icon} {cat.name}
                          </span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${cat.type === 'income' ? 'bg-success/15 text-success' : 'bg-error/15 text-error'}`}>
                            {cat.type === 'income' ? 'Ingreso' : 'Egreso'}
                          </span>
                          <button type="button" onClick={() => handleRemoveCategory(cat.id)} className="text-text-tertiary hover:text-error">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-text-tertiary">Agrega categorías usando el formulario inferior.</p>
                  )}

                  {/* Add Category Form */}
                  <div className="space-y-2.5 border-t border-border/40 pt-2">
                    <div className="flex gap-2">
                      <input 
                        type="text"
                        value={newCatName}
                        onChange={(e) => setNewCatName(e.target.value)}
                        placeholder="Nombre de la categoría..."
                        className="flex-1 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs text-text-primary focus:border-brand-orange"
                      />
                      <select
                        value={newCatType}
                        onChange={(e) => setNewCatType(e.target.value as 'income' | 'expense')}
                        className="rounded-lg border border-border bg-surface px-2 py-1 text-xs text-text-primary font-bold"
                      >
                        <option value="income">Ingreso</option>
                        <option value="expense">Egreso</option>
                      </select>
                    </div>
                    
                    {/* Icon Selection */}
                    <div>
                      <span className="block text-[10px] font-bold text-text-tertiary mb-1">Selecciona un Icono</span>
                      <div className="flex flex-wrap gap-1.5">
                        {PRESET_ICONS.map((icon) => (
                          <button
                            key={icon}
                            type="button"
                            onClick={() => setSelectedIcon(icon)}
                            className={`p-1.5 rounded-lg text-sm transition-all ${selectedIcon === icon ? 'bg-brand-orange/20 ring-2 ring-brand-orange scale-110' : 'bg-surface-secondary hover:bg-surface-tertiary'}`}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Color Selection */}
                    <div>
                      <span className="block text-[10px] font-bold text-text-tertiary mb-1">Selecciona un Color</span>
                      <div className="flex gap-2">
                        {PRESET_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setSelectedColor(color)}
                            className={`h-5 w-5 rounded-full transition-all border border-border ${selectedColor === color ? 'ring-2 ring-brand-orange ring-offset-2 scale-110' : 'hover:scale-105'}`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>

                    <button 
                      type="button" 
                      onClick={handleAddCategory} 
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-brand-orange text-white text-xs font-bold hover:bg-brand-orange/95 active:scale-[0.98]"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Agregar Categoría
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION B: Payment Methods */}
            <div className="border-t border-border/40 pt-3 space-y-3">
              <label className="flex items-start gap-3 rounded-xl border border-border bg-surface-secondary/20 p-3 hover:bg-surface-secondary/40 transition-colors cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={loadDefaultPaymentMethods}
                  onChange={(e) => setLoadDefaultPaymentMethods(e.target.checked)}
                  className="mt-1 rounded border-border text-brand-orange focus:ring-brand-orange/20"
                />
                <div>
                  <span className="flex items-center gap-1.5 text-sm font-bold text-text-primary">
                    <Wallet className="h-4 w-4 text-brand-orange" />
                    Billeteras y Métodos de Pago (VES/{baseCurrency})
                  </span>
                  <p className="text-[11px] text-text-secondary mt-0.5">
                    Pre-cargar métodos de pago básicos sin sufijos de currency redundantes en el name (Efectivo, Zelle, Binance Pay, Bancos locales). Las divisas se asocian de forma nativa a {baseCurrency}.
                  </p>
                </div>
              </label>

              {!loadDefaultPaymentMethods && (
                <div className="rounded-xl border border-dashed border-border p-3 bg-surface space-y-3 animate-fade-in">
                  <span className="text-xs font-bold text-brand-orange block">Billeteras y Métodos Personalizados</span>
                  
                  {/* MP List */}
                  {customPaymentMethods.length > 0 ? (
                    <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto pb-1">
                      {customPaymentMethods.map((mp) => (
                        <div key={mp.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-surface-secondary text-text-primary border border-border">
                          <span>{mp.name}</span>
                          <span className="text-[9px] bg-brand-orange/10 text-brand-orange px-1.5 py-0.5 rounded-full font-bold">
                            {mp.currency}
                          </span>
                          <button type="button" onClick={() => handleRemovePaymentMethod(mp.id)} className="text-text-tertiary hover:text-error">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-text-tertiary">Agrega métodos usando el formulario inferior.</p>
                  )}

                  {/* Add MP Form */}
                  <div className="space-y-2 border-t border-border/40 pt-2">
                    <div className="flex gap-2">
                      <input 
                        type="text"
                        value={newMpName}
                        onChange={(e) => setNewMpName(e.target.value)}
                        placeholder="Nombre de la billetera o banco (ej. Efectivo, Banesco)..."
                        className="flex-1 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs text-text-primary focus:border-brand-orange"
                      />
                      <select
                        value={newMpCurrency}
                        onChange={(e) => setNewMpCurrency(e.target.value)}
                        className="rounded-lg border border-border bg-surface px-2 py-1 text-xs text-text-primary font-bold"
                      >
                        <option value="VES">VES (Bolívares)</option>
                        <option value="USD">USD (Dólares)</option>
                        <option value="EUR">EUR (Euros)</option>
                      </select>
                    </div>
                    <p className="text-[10px] text-text-tertiary italic leading-normal">
                      * El type de currency (Local/Divisa), sucursal de visibilidad global y estados de auditoría se configuran automáticamente tras registrarse.
                    </p>
                    <button 
                      type="button" 
                      onClick={handleAddPaymentMethod} 
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-brand-orange text-white text-xs font-bold hover:bg-brand-orange/95 active:scale-[0.98]"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Agregar Método de Pago
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

        {/* STEP 4: Review & Finish */}
        {step === 4 && (
          <div className="space-y-4 animate-scale-in">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-brand-orange/10 text-brand-orange shadow-sm">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-text-primary">Confirmar Configuración</h3>
                <p className="text-xs text-text-secondary">Verifica que todos los datos sean correctos antes de inicializar.</p>
              </div>
            </div>
            
            <div className="rounded-xl border border-border/60 bg-surface-secondary/20 p-4 space-y-3 text-xs text-text-secondary">
              <div className="flex justify-between">
                <span>Sede Principal:</span>
                <strong className="text-text-primary">{branchName} ({branchCode})</strong>
              </div>
              <div className="flex justify-between">
                <span>Moneda Base (Contable):</span>
                <strong className="text-text-primary">{baseCurrency}</strong>
              </div>
              <div className="flex justify-between">
                <span>Moneda Local:</span>
                <strong className="text-text-primary">VES (Bolívar)</strong>
              </div>
              <div className="flex justify-between">
                <span>Año Inicial Historial BCV:</span>
                <strong className="text-text-primary">{startYear}</strong>
              </div>
              <div className="flex justify-between">
                <span>Categorías a Crear:</span>
                <strong className="text-text-primary">
                  {loadDefaultCategories ? '7 estándar (Ventas, Nóminas, Alquiler, etc.)' : `${customCategories.length} personalizadas`}
                </strong>
              </div>
              <div className="flex justify-between">
                <span>Métodos de Pago a Crear:</span>
                <strong className="text-text-primary">
                  {loadDefaultPaymentMethods ? `7 estándar (Efectivo VES/${baseCurrency}, Zelle, Binance Pay, Bancos)` : `${customPaymentMethods.length} personalizados`}
                </strong>
              </div>
            </div>

            {submitStatus && (
              <div className="mt-4 p-3 bg-brand-orange/5 border border-brand-orange/20 rounded-xl flex items-center gap-2.5 text-xs text-brand-orange font-bold animate-pulse">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{submitStatus}</span>
              </div>
            )}
          </div>
        )}

        {/* Stepper Footer Controls */}
        <div className="mt-6 border-t border-border/40 pt-4 flex justify-between gap-3">
          {step > 1 ? (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={handlePrev}
              className="flex items-center gap-1.5 rounded-xl border border-border bg-white px-4 py-2 text-sm font-semibold text-text-secondary hover:bg-surface-secondary/40 transition-colors disabled:opacity-50 active:scale-95"
            >
              <ArrowLeft className="h-4 w-4" />
              Atrás
            </button>
          ) : (
            <div />
          )}

          {step < 4 ? (
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center gap-1.5 rounded-xl bg-brand-orange px-5 py-2 text-sm font-bold text-white shadow-sm hover:bg-brand-orange/95 transition-all active:scale-95 ml-auto"
            >
              Siguiente
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={handleFinish}
              className="flex items-center gap-1.5 rounded-xl bg-brand-orange px-5 py-2 text-sm font-bold text-white shadow-sm hover:bg-brand-orange/95 transition-all disabled:opacity-60 active:scale-95 ml-auto"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  Finalizar e Inicializar
                  <CheckCircle className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
